import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    TextField,
    Typography,
    Box,
    Button,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormControl,
    FormLabel,
    Select,
    MenuItem,
    Card,
    CardContent,
    Divider,
    InputAdornment
} from '@mui/material';
import { createTheme, ThemeProvider, styled } from '@mui/material/styles';
import axios from 'axios';
import { url } from '../utils/constants';
import {
    LockReset,
    WhatsApp,
    Email,
    Phone,
    ArrowBack
} from '@mui/icons-material';
import logoImage from '../../logo192.png';

const theme = createTheme();

// Styled components para elementos modernos
const ModernButton = styled(Button)(({ theme }) => ({
    borderRadius: '25px',
    textTransform: 'none',
    fontWeight: 600,
    padding: '12px 32px',
    fontSize: '1.1rem',
    transition: 'all 0.3s ease',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
    },
}));

const ModernTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: '15px',
        backgroundColor: 'rgba(255,255,255,0.9)',
        transition: 'all 0.3s ease',
        '&:hover': {
            backgroundColor: 'rgba(255,255,255,1)',
            transform: 'translateY(-1px)',
        },
        '&.Mui-focused': {
            backgroundColor: 'rgba(255,255,255,1)',
            transform: 'translateY(-1px)',
        },
    },
}));

const ModernSelect = styled(Select)(({ theme }) => ({
    borderRadius: '15px',
    backgroundColor: 'rgba(255,255,255,0.9)',
    transition: 'all 0.3s ease',
    '&:hover': {
        backgroundColor: 'rgba(255,255,255,1)',
        transform: 'translateY(-1px)',
    },
    '&.Mui-focused': {
        backgroundColor: 'rgba(255,255,255,1)',
        transform: 'translateY(-1px)',
    },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
}));

const masks = {
    '+55': { name: 'Brasil', mask: '(xx) xxxxx-xxxx' },
    '+595': { name: 'Paraguay', mask: '(xxx) xxx-xxx' },
    '+1': { name: 'EUA', mask: '(xxx) xxx-xxxx' }
    // Adicione outras máscaras conforme necessário
};

const validateInput = (value, ddi) => {
    if (!value) return "Required!";

    // Para Brasil (+55), aceitar números com 10 ou 11 dígitos
    if (ddi === '+55') {
        const digitsOnly = value.replace(/[^\d]/g, '');
        if (digitsOnly.length < 10 || digitsOnly.length > 11) {
            return "Número inválido. Use 10 ou 11 dígitos.";
        }
    }
    // Para outros países, usar a validação original
    else if (value.length !== masks[ddi].mask.length) {
        return `Invalid phone format. ${masks[ddi].mask}`;
    }
    return "";
};


const ForgotPassword = () => {
    const navigate = useNavigate();
    const [phone, setPhone] = useState("");
    const [error, setError] = useState("");
    const [deliveryMethod, setDeliveryMethod] = useState("whatsapp");
    const [ddi, setDdi] = useState("+55");
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const formattedPhoneNumber = useRef('');

    useEffect(() => {
        formattedPhoneNumber.current = (ddi + phone).replace(/[()\s+-]/g, "");
    }, [ddi, phone]);

    const normalizeInput = (value, ddi) => {
        if (!value) return value;
        const currentValue = value.replace(/[^\d]/g, '');

        // Obter a máscara correspondente ao DDI selecionado
        const mask = masks[ddi].mask;
        if (!mask) {
            // Se não houver uma máscara correspondente, retornar o valor atual sem formatação
            return currentValue;
        }

        let formattedValue = '';
        let maskIndex = 0;

        // Permitir mais caracteres que a máscara para números completos
        const maxLength = Math.max(mask.length, currentValue.length);

        for (let i = 0; i < maxLength && maskIndex < currentValue.length; i++) {
            if (i < mask.length && mask[i] === 'x') {
                // Se o caractere na máscara for 'x', adicionar o próximo dígito do valor atual
                formattedValue += currentValue[maskIndex];
                maskIndex++;
            } else if (i < mask.length) {
                // Se o caractere na máscara não for 'x', adicionar o próprio caractere
                formattedValue += mask[i];
            } else {
                // Se exceder a máscara, adicionar os dígitos restantes
                formattedValue += currentValue[maskIndex];
                maskIndex++;
            }
        }
        return formattedValue;
    };

    const handleChange = (event) => {
        const { value } = event.target;
        //setPhone(value);
        setPhone(normalizeInput(value, ddi));

    };

    const handleRadioChange = (event) => {
        const { value } = event.target;
        setDeliveryMethod(value);
        handleReset();
    };

    const handleDdiChange = (event) => {
        const { value } = event.target;
        setDdi(value);
        handleReset();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        let error = '';

        if (deliveryMethod === 'whatsapp') {
            error = validateInput(phone, ddi);
        } else if (deliveryMethod === 'email') {
            error += 'email ' + isEmailValid(email);
        }
        setError(error);

        if (!error) {
            try {
                await axios.post(url + '/api/v1/user/forgot-password', { phoneNumber: formattedPhoneNumber.current, email, deliveryMethod });
                if (deliveryMethod === 'whatsapp')
                    setMessage('Uma mensagem de redefinição de senha foi enviado para o seu whatsapp.');
                else
                    setMessage('Um e-mail de redefinição de senha foi enviado para o seu endereço de e-mail.');
            } catch (error) {
                if (deliveryMethod === 'whatsapp')
                    setMessage('Ocorreu um erro ao enviar mensagem de redefinição de senha. Verifique o whatsapp. ' + error);
                else
                    setMessage('Ocorreu um erro ao enviar o e-mail de redefinição de senha. Verifique o endereço de e-mail. ' + error);
            }
        }
    };

    const handleReset = () => {
        setPhone("");
        setEmail('');
        setError("");
    };

    const isEmailValid = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleEmailChange = (event) => {
        const { value } = event.target;
        if (!isEmailValid(value)) {
            console.log('E-mail inválido');
        }
        setEmail(value);
    };

    const handleGoHome = () => {
        navigate('/')
    }

    return (
        <ThemeProvider theme={theme}>
            <Box sx={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #1976D2 0%, #42A5F5 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: { xs: 1, sm: 2 },
                // Ajustar para mobile
                pt: { xs: 'calc(64px + max(env(safe-area-inset-top, 0px), 10px))', sm: 0 },
                pb: { xs: 2, sm: 0 }
            }}>
                <Card
                    elevation={10}
                    sx={{
                        maxWidth: 500,
                        width: '100%',
                        borderRadius: 4,
                        overflow: 'hidden',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                        backdropFilter: 'blur(10px)',
                        backgroundColor: 'rgba(255,255,255,0.95)'
                    }}
                >
                    <CardContent sx={{ p: 4 }}>
                        <Typography
                            variant="h5"
                            component="h1"
                            sx={{
                                textAlign: 'center',
                                mb: 3,
                                fontWeight: 600,
                                color: '#333',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1
                            }}
                        >
                            <LockReset color="primary" />
                            Esqueceu sua senha?
                        </Typography>

                        <Box component="form" onSubmit={handleSubmit} noValidate>
                            <FormControl component="fieldset" sx={{ mb: 3 }}>
                                <FormLabel
                                    component="legend"
                                    sx={{
                                        color: '#1976D2',
                                        fontWeight: 600,
                                        mb: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1
                                    }}
                                >
                                    <Phone color="primary" />
                                    Como deseja solicitar a troca?
                                </FormLabel>
                                <RadioGroup
                                    row
                                    aria-label="delivery-method"
                                    name="delivery-method"
                                    value={deliveryMethod}
                                    onChange={handleRadioChange}
                                    sx={{ justifyContent: 'center' }}
                                >
                                    {false && (
                                        <FormControlLabel
                                            value="email"
                                            control={<Radio />}
                                            label={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Email />
                                                    E-mail
                                                </Box>
                                            }
                                        />
                                    )}
                                    <FormControlLabel
                                        value="whatsapp"
                                        control={<Radio />}
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <WhatsApp />
                                                WhatsApp
                                            </Box>
                                        }
                                    />
                                </RadioGroup>
                            </FormControl>

                            {deliveryMethod === "whatsapp" && (
                                <Box sx={{ mb: 3 }}>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            mb: 2,
                                            fontWeight: 500,
                                            color: '#333',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1
                                        }}
                                    >
                                        <Phone color="primary" />
                                        Informe seu telefone:
                                    </Typography>
                                    <Box sx={{
                                        display: 'flex',
                                        gap: 2,
                                        flexDirection: { xs: 'column', sm: 'row' }
                                    }}>
                                        <FormControl sx={{
                                            minWidth: { xs: '100%', sm: 150 },
                                            width: { xs: '100%', sm: 'auto' }
                                        }}>
                                            <ModernSelect
                                                labelId="ddi-label"
                                                id="ddi"
                                                name="ddi"
                                                value={ddi}
                                                onChange={handleDdiChange}
                                            >
                                                {Object.keys(masks).map((countryCode) => (
                                                    <MenuItem key={countryCode} value={countryCode}>
                                                        {`${countryCode} - ${masks[countryCode].name}`}
                                                    </MenuItem>
                                                ))}
                                            </ModernSelect>
                                        </FormControl>
                                        <Box sx={{
                                            flexGrow: 1,
                                            width: { xs: '100%', sm: 'auto' }
                                        }}>
                                            <ModernTextField
                                                type="tel"
                                                placeholder={masks[ddi].mask}
                                                value={phone}
                                                fullWidth
                                                name="phoneNumber"
                                                onChange={handleChange}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <Phone color="action" />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                            {error && (
                                                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                                                    {error}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            )}

                            {deliveryMethod === 'email' && (
                                <Box sx={{ mb: 3 }}>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            mb: 2,
                                            fontWeight: 500,
                                            color: '#333',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1
                                        }}
                                    >
                                        <Email color="primary" />
                                        Seu e-mail:
                                    </Typography>
                                    <ModernTextField
                                        type="email"
                                        placeholder="exemplo@exemplo.com"
                                        value={email}
                                        fullWidth
                                        onChange={handleEmailChange}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Email color="action" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Box>
                            )}

                            <Divider sx={{ my: 3 }} />

                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                                <ModernButton
                                    type="button"
                                    variant="outlined"
                                    onClick={handleGoHome}
                                    startIcon={<ArrowBack />}
                                    sx={{
                                        borderColor: '#757575',
                                        color: '#757575',
                                        '&:hover': {
                                            borderColor: '#424242',
                                            backgroundColor: 'rgba(117, 117, 117, 0.04)',
                                        }
                                    }}
                                >
                                    Voltar
                                </ModernButton>
                                <ModernButton
                                    type="submit"
                                    variant="contained"
                                    sx={{
                                        backgroundColor: '#1976D2',
                                        '&:hover': {
                                            backgroundColor: '#1565C0',
                                        }
                                    }}
                                >
                                    Enviar
                                </ModernButton>
                            </Box>

                            {message && (
                                <Box sx={{
                                    mt: 3,
                                    p: 2,
                                    backgroundColor: message.includes('erro') ? 'rgba(244, 67, 54, 0.1)' : 'rgba(76, 175, 80, 0.1)',
                                    borderRadius: 2,
                                    border: `1px solid ${message.includes('erro') ? '#F44336' : '#4CAF50'}`
                                }}>
                                    <Typography
                                        variant="body2"
                                        color={message.includes('erro') ? 'error' : 'success'}
                                        sx={{ textAlign: 'center' }}
                                    >
                                        {message}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        </ThemeProvider>
    );
};

export default ForgotPassword;
